import { forwardRef, useMemo, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Check, Circle, Globe, Loader2, Pencil, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { MultiSelect } from "@/components/ui/multi-select";
import { VirtualizedCombobox } from "@/components/ui/virtualized-combobox";
import {
  BusinessModels,
  CountryOptions,
  GICSSubIndustries,
  RevenueModels,
  StageOptions,
  cn,
  getStageText,
} from "@/lib/utils";
import { DEFAULT_CURRENCY, getCurrencyByCode } from "@/lib/constants/currencies";
import { StepCard, StepCardLabel } from "../step-card";
import { ExampleChips, Hint, KeyHint } from "../hints";
import { chipVariants, motionSafe, staggerContainer } from "../motion";

/** The thesis fields the chat collects, in the shape `edit_opportunity_data` wants. */
export interface ThesisDraft {
  target_gics_sub_industries: string[];
  target_stage_companies: string[];
  target_model_companies: string[];
  target_revenue_models: string[];
  geographical_location: string[];
  min_cheque_size: number;
  max_cheque_size: number;
  currency: string;
  additional_info: string;
}

const PLACEHOLDERS = ["no information provided", "not applicable", "n/a", "none", ""];

/** The AI occasionally emits filler strings for fields it couldn't determine. */
export function stripPlaceholders(values: string[] | undefined): string[] {
  if (!values) return [];
  return values.filter((v) => !PLACEHOLDERS.includes(v.toLowerCase().trim()));
}

// ────────────────────────────────────────────────────────────────
// Phase 1 — capture
// ────────────────────────────────────────────────────────────────

/** Example sites for the website field — real funds, so the read is meaningful. */
const EXAMPLE_SITES = ["sequoiacap.com", "a16z.com", "ycombinator.com"];

/** What a strong thesis mentions, and how we spot each one in free text. */
const THESIS_SIGNALS: Array<{ label: string; pattern: RegExp }> = [
  { label: "Stage", pattern: /\b(pre-?seed|seed|series [a-e]|early[- ]stage|growth|late[- ]stage|angel)\b/i },
  {
    label: "Sector",
    pattern:
      /\b(fintech|saas|b2b|b2c|health|healthcare|biotech|ai|climate|consumer|marketplace|deep ?tech|crypto|web3|edtech|proptech|insurtech|logistics|hardware|software|enterprise|mobility|energy|food|agri\w*)\b/i,
  },
  {
    label: "Geography",
    pattern:
      /\b(mena|gcc|uae|ksa|saudi|egypt|africa|europe|uk|us|usa|united states|north america|latam|latin america|asia|sea|india|global|emerging markets|canada|nordics?)\b/i,
  },
  { label: "Cheque size", pattern: /([$€£]\s?\d)|(\d+(\.\d+)?\s?(k|m|mm|million)\b)|\b(cheques?|checks?|tickets?)\b/i },
];

function looksLikeSite(value: string) {
  return /^(https?:\/\/)?(www\.)?[a-z0-9-]+(\.[a-z0-9-]+)+(\/\S*)?$/i.test(value.trim());
}

export function ThesisInput({
  onAnalyze,
  onManual,
  isAnalyzing,
}: {
  onAnalyze: (context: string) => void;
  onManual: () => void;
  isAnalyzing: boolean;
}) {
  const shouldReduce = useReducedMotion();
  const [site, setSite] = useState("");
  const [description, setDescription] = useState("");

  const siteIsValid = looksLikeSite(site);
  const siteHasError = site.trim().length > 0 && !siteIsValid;
  const covered = useMemo(
    () => THESIS_SIGNALS.map((s) => ({ ...s, hit: s.pattern.test(description) })),
    [description]
  );

  // The site leads; the description sharpens what the page alone can't say.
  const context = [siteIsValid ? `Website: ${site.trim()}` : "", description.trim()]
    .filter(Boolean)
    .join("\n\n");
  const canAnalyze = context.length > 0 && !siteHasError && !isAnalyzing;
  const submit = () => canAnalyze && onAnalyze(context);

  return (
    <StepCard>
      <div className="space-y-5">
        <div className="space-y-2">
          <StepCardLabel>Your website</StepCardLabel>
          <div className="relative">
            <Globe className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/70" />
            <Input
              value={site}
              onChange={(e) => setSite(e.target.value)}
              placeholder="yourfund.com"
              aria-label="Your fund's website"
              className="h-11 pl-9 text-[15px]"
              disabled={isAnalyzing}
              autoFocus
              aria-invalid={siteHasError}
              onKeyDown={(e) => {
                if (e.key === "Enter") submit();
              }}
            />
            <AnimatePresence>
              {siteIsValid && (
                <motion.span
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-primary"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: shouldReduce ? 0 : 0.15 }}
                  title="I'll read this page"
                >
                  <Check className="h-4 w-4" />
                </motion.span>
              )}
            </AnimatePresence>
          </div>
          {siteHasError ? (
            <Hint className="text-destructive">That doesn't look like a web address yet.</Hint>
          ) : (
            <ExampleChips examples={EXAMPLE_SITES} onPick={setSite} disabled={isAnalyzing} />
          )}
        </div>

        <div className="flex items-center gap-3" aria-hidden>
          <span className="h-px flex-1 bg-border" />
          <span className="text-xs text-muted-foreground">and, or instead, describe it</span>
          <span className="h-px flex-1 bg-border" />
        </div>

        <div className="space-y-2">
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="We back pre-seed and seed fintech in MENA, cheques of $250k–$1M."
            aria-label="Describe your investment thesis"
            className="min-h-[96px] resize-none text-[15px] leading-relaxed"
            disabled={isAnalyzing}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit();
            }}
          />
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1" aria-label="What your description covers">
            {covered.map(({ label, hit }) => (
              <span
                key={label}
                className={cn(
                  "inline-flex items-center gap-1 text-xs transition-colors",
                  hit ? "text-primary" : "text-muted-foreground/70"
                )}
              >
                {hit ? <Check className="h-3 w-3" /> : <Circle className="h-2.5 w-2.5" />}
                {label}
              </span>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-border/60 pt-4">
          <button
            type="button"
            onClick={onManual}
            disabled={isAnalyzing}
            className="text-xs text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline"
          >
            I'd rather pick from a list
          </button>

          <div className="flex items-center gap-3">
            <KeyHint keys={["mod", "↵"]} />
            <Button onClick={submit} disabled={!canAnalyze} className="gap-2">
              {isAnalyzing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ArrowRight className="h-4 w-4" />
              )}
              {isAnalyzing ? "Reading…" : "Analyze"}
            </Button>
          </div>
        </div>
      </div>
    </StepCard>
  );
}

// ────────────────────────────────────────────────────────────────
// Phase 2 — review what the AI extracted
// ────────────────────────────────────────────────────────────────

/**
 * A removable criterion. Exits by shrinking out so the row reflows smoothly.
 * Ref-forwarded for AnimatePresence's `popLayout` measurement.
 */
const Chip = forwardRef<
  HTMLSpanElement,
  { label: string; onRemove: () => void; shouldReduce: boolean | null }
>(function Chip({ label, onRemove, shouldReduce }, ref) {
  return (
    <motion.span
      ref={ref}
      layout
      variants={motionSafe(chipVariants, shouldReduce)}
      exit="exit"
      whileHover={shouldReduce ? undefined : { y: -1 }}
      className="group inline-flex items-center gap-1 rounded-full border border-primary/25 bg-primary/8 py-1 pl-2.5 pr-1.5 text-xs font-medium text-foreground"
    >
      {label}
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${label}`}
        className="rounded-full p-0.5 text-muted-foreground/60 transition-colors hover:bg-primary/15 hover:text-foreground"
      >
        <X className="h-3 w-3" />
      </button>
    </motion.span>
  );
});

function CriteriaGroup({
  label,
  values,
  onRemove,
  shouldReduce,
}: {
  label: string;
  values: string[];
  onRemove: (value: string) => void;
  shouldReduce: boolean | null;
}) {
  if (values.length === 0) return null;

  return (
    <motion.div layout className="space-y-1.5">
      <StepCardLabel>{label}</StepCardLabel>
      <motion.div
        className="flex flex-wrap gap-1.5"
        variants={motionSafe(staggerContainer, shouldReduce)}
        initial="hidden"
        animate="visible"
      >
        <AnimatePresence mode="popLayout">
          {values.map((value) => (
            <Chip
              key={value}
              label={value}
              onRemove={() => onRemove(value)}
              shouldReduce={shouldReduce}
            />
          ))}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
}

export function ThesisReview({
  draft,
  onChange,
  onConfirm,
  isSubmitting,
  /** Open the full editors immediately — used for the "pick from a list" path. */
  startExpanded = false,
}: {
  draft: ThesisDraft;
  onChange: (next: ThesisDraft) => void;
  onConfirm: () => void;
  isSubmitting: boolean;
  startExpanded?: boolean;
}) {
  const shouldReduce = useReducedMotion();
  const [expanded, setExpanded] = useState(startExpanded);

  const industryOptions = useMemo(
    () => GICSSubIndustries.map((si) => ({ value: si.code, label: si.name })),
    []
  );
  const industryNameByCode = useMemo(
    () => new Map(GICSSubIndustries.map((si) => [si.code, si.name])),
    []
  );

  const currency = getCurrencyByCode(draft.currency) ?? getCurrencyByCode(DEFAULT_CURRENCY);
  const symbol = currency?.symbol ?? "$";
  const formatCheque = (n: number) => (n > 0 ? `${symbol}${n.toLocaleString("en-US")}` : "");
  const parseCheque = (s: string) => Number(s.replace(/[^0-9]/g, "")) || 0;

  const set = <K extends keyof ThesisDraft>(key: K, value: ThesisDraft[K]) =>
    onChange({ ...draft, [key]: value });

  const removeFrom = (key: keyof ThesisDraft, value: string) =>
    set(key, (draft[key] as string[]).filter((v) => v !== value) as never);

  const hasAnything =
    draft.target_gics_sub_industries.length > 0 ||
    draft.target_stage_companies.length > 0 ||
    draft.target_model_companies.length > 0 ||
    draft.geographical_location.length > 0;

  return (
    <StepCard>
      <motion.div layout className="space-y-4">
        {hasAnything && (
          <motion.div layout className="space-y-3.5">
            <CriteriaGroup
              label="Stages"
              values={draft.target_stage_companies}
              onRemove={(v) => removeFrom("target_stage_companies", v)}
              shouldReduce={shouldReduce}
            />
            <CriteriaGroup
              label="Sectors"
              values={draft.target_gics_sub_industries.map(
                (code) => industryNameByCode.get(code) ?? code
              )}
              onRemove={(name) => {
                const code = draft.target_gics_sub_industries.find(
                  (c) => (industryNameByCode.get(c) ?? c) === name
                );
                if (code) removeFrom("target_gics_sub_industries", code);
              }}
              shouldReduce={shouldReduce}
            />
            <CriteriaGroup
              label="Business models"
              values={draft.target_model_companies}
              onRemove={(v) => removeFrom("target_model_companies", v)}
              shouldReduce={shouldReduce}
            />
            <CriteriaGroup
              label="Geographies"
              values={draft.geographical_location}
              onRemove={(v) => removeFrom("geographical_location", v)}
              shouldReduce={shouldReduce}
            />
          </motion.div>
        )}

        {/* Cheque size always shows — it's the one criterion the AI most
            often can't infer, and it materially affects fit scoring. */}
        <motion.div layout className="space-y-1.5">
          <StepCardLabel>Cheque size</StepCardLabel>
          <div className="flex items-center gap-2">
            <Input
              value={formatCheque(draft.min_cheque_size)}
              onChange={(e) => set("min_cheque_size", parseCheque(e.target.value))}
              placeholder={`${symbol}250,000`}
              inputMode="numeric"
              className="h-9 text-sm"
            />
            <span className="text-sm text-muted-foreground">to</span>
            <Input
              value={formatCheque(draft.max_cheque_size)}
              onChange={(e) => set("max_cheque_size", parseCheque(e.target.value))}
              placeholder={`${symbol}2,000,000`}
              inputMode="numeric"
              className="h-9 text-sm"
            />
          </div>
        </motion.div>

        <AnimatePresence initial={false}>
          {expanded && (
            <motion.div
              key="editors"
              layout
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: shouldReduce ? 0 : 0.25, ease: "easeOut" }}
              className="space-y-3 overflow-hidden border-t border-border/60 pt-3"
            >
              <div className="space-y-1.5">
                <StepCardLabel>Sectors</StepCardLabel>
                <VirtualizedCombobox
                  options={industryOptions}
                  selected={draft.target_gics_sub_industries}
                  onToggle={(code) =>
                    set(
                      "target_gics_sub_industries",
                      draft.target_gics_sub_industries.includes(code)
                        ? draft.target_gics_sub_industries.filter((c) => c !== code)
                        : [...draft.target_gics_sub_industries, code]
                    )
                  }
                  placeholder="Add sectors"
                  searchPlaceholder="Search sectors…"
                  multiple
                />
              </div>

              <div className="space-y-1.5">
                <StepCardLabel>Stages</StepCardLabel>
                <MultiSelect
                  // MultiSelect is uncontrolled (defaultValue only), so it has
                  // to remount when a chip is removed above.
                  key={`stages-${draft.target_stage_companies.join(",")}`}
                  id="onboarding-stages"
                  options={StageOptions.map((o) => ({ label: o.label, value: o.value }))}
                  defaultValue={draft.target_stage_companies}
                  onValueChange={(v) => set("target_stage_companies", v)}
                  placeholder="Add stages"
                  maxCount={20}
                />
              </div>

              <div className="space-y-1.5">
                <StepCardLabel>Business models</StepCardLabel>
                <MultiSelect
                  key={`models-${draft.target_model_companies.join(",")}`}
                  id="onboarding-business-models"
                  options={BusinessModels}
                  defaultValue={draft.target_model_companies}
                  onValueChange={(v) => set("target_model_companies", v)}
                  placeholder="Add business models"
                  maxCount={20}
                />
              </div>

              <div className="space-y-1.5">
                <StepCardLabel>Revenue models</StepCardLabel>
                <MultiSelect
                  key={`revenue-${draft.target_revenue_models.join(",")}`}
                  id="onboarding-revenue-models"
                  options={RevenueModels}
                  defaultValue={draft.target_revenue_models}
                  onValueChange={(v) => set("target_revenue_models", v)}
                  placeholder="Add revenue models"
                  maxCount={20}
                />
              </div>

              <div className="space-y-1.5">
                <StepCardLabel>Geographies</StepCardLabel>
                <MultiSelect
                  key={`geo-${draft.geographical_location.join(",")}`}
                  id="onboarding-geographies"
                  options={CountryOptions}
                  defaultValue={draft.geographical_location}
                  onValueChange={(v) => set("geographical_location", v)}
                  placeholder="Add geographies"
                  maxCount={20}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <motion.div layout className="flex items-center justify-between gap-3 pt-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setExpanded((v) => !v)}
            className="gap-1.5 text-xs text-muted-foreground"
          >
            <Pencil className="h-3.5 w-3.5" />
            {expanded ? "Done editing" : "Edit criteria"}
          </Button>

          <Button onClick={onConfirm} disabled={isSubmitting} className="gap-2">
            {isSubmitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                Looks right
                <ArrowRight className="h-4 w-4" />
              </>
            )}
          </Button>
        </motion.div>
      </motion.div>
    </StepCard>
  );
}

/** Builds the review draft from whatever the AI wrote onto the workspace. */
export function draftFromWorkspace(workspace: {
  target_gics_sub_industries?: string[];
  target_stage_companies?: string[];
  target_model_companies?: string[];
  target_revenue_models?: string[];
  geographical_location?: string[];
  min_cheque_size?: number;
  max_cheque_size?: number;
  currency?: string;
  additional_info?: string;
}): ThesisDraft {
  return {
    target_gics_sub_industries: stripPlaceholders(workspace.target_gics_sub_industries),
    // Legacy workspaces stored stages as numbers; normalise before display.
    target_stage_companies: stripPlaceholders(
      (workspace.target_stage_companies ?? []).map((v) => getStageText(v))
    ).filter((v) => v !== "Unknown"),
    target_model_companies: stripPlaceholders(workspace.target_model_companies),
    target_revenue_models: stripPlaceholders(workspace.target_revenue_models),
    geographical_location: stripPlaceholders(workspace.geographical_location),
    min_cheque_size: workspace.min_cheque_size ?? 0,
    max_cheque_size: workspace.max_cheque_size ?? 0,
    currency: workspace.currency || DEFAULT_CURRENCY,
    additional_info: workspace.additional_info ?? "",
  };
}

/** One-line summary of a thesis, echoed back as the user's chat bubble. */
export function summariseThesis(draft: ThesisDraft): string {
  const parts: string[] = [];
  if (draft.target_stage_companies.length) parts.push(draft.target_stage_companies.join(", "));

  if (draft.target_gics_sub_industries.length) {
    const names = draft.target_gics_sub_industries
      .map((code) => GICSSubIndustries.find((si) => si.code === code)?.name ?? code)
      .slice(0, 3);
    parts.push(names.join(", "));
  }

  if (draft.geographical_location.length) parts.push(`in ${draft.geographical_location.join(", ")}`);

  if (draft.min_cheque_size || draft.max_cheque_size) {
    const symbol = getCurrencyByCode(draft.currency)?.symbol ?? "$";
    const fmt = (n: number) => `${symbol}${n.toLocaleString("en-US")}`;
    parts.push(
      draft.min_cheque_size && draft.max_cheque_size
        ? `${fmt(draft.min_cheque_size)}–${fmt(draft.max_cheque_size)}`
        : fmt(draft.min_cheque_size || draft.max_cheque_size)
    );
  }

  return parts.length ? parts.join(" · ") : "Thesis confirmed";
}
